import React, { useEffect, useState } from "react";
import { useTranslation, Trans } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Wallet, Package, Truck, Flame, Sparkles, Gift } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import WorldMapCard from "../components/WorldMapCard";
import RotatingBanners from "../components/RotatingBanners";

export default function Landing() {
  const { t } = useTranslation();
  const [banners, setBanners] = useState([]);
  const { user } = useAuth();
  const isArchitect = user && user.role !== "admin";
  const isTrader = isArchitect && user.account_type === "trader";
  const isRealEstate = isArchitect && user.account_type === "real_estate";
  const isStudio = isArchitect && user.account_type === "studio";
  const primaryCta = isArchitect
    ? { to: isRealEstate ? "/contatti" : "/progetti", label: isRealEstate ? t("landing.cta_contacts") : t("landing.cta_projects"), testid: "hero-cta-projects" }
    : { to: "/register", label: t("landing.cta_become_partner"), testid: "hero-cta-register" };
  const footerCta = isArchitect
    ? { to: isRealEstate ? "/contatti" : "/progetti", label: isRealEstate ? t("landing.cta_contacts") : t("landing.cta_projects"), testid: "footer-cta-projects" }
    : { to: "/register", label: t("landing.cta_request_access"), testid: "footer-cta-register" };

  // Configurazione hero/value/CTA per ruolo. Tutto il copy viene dall'i18n: la
  // chiave usata cambia in funzione del ruolo per offrire un'esperienza "home
  // page dedicata" post-login (RE vede solo testi RE, trader solo testi trader,
  // studio solo testi studio).
  const variant = isRealEstate ? "re" : isTrader ? "trader" : isStudio ? "studio" : "default";
  const heroImage = isRealEstate
    ? "/realestate-hero.jpg"
    : "https://images.pexels.com/photos/33685861/pexels-photo-33685861.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1400";
  const k = (suffix) => {
    // Restituisce la chiave i18n con suffisso per ruolo, con fallback alla default.
    const keyRe = `landing.${suffix}_re`;
    const keyTrader = `landing.${suffix}_trader`;
    const keyStudio = `landing.${suffix}_studio`;
    if (variant === "re" && t(keyRe) !== keyRe) return keyRe;
    if (variant === "trader" && t(keyTrader) !== keyTrader) return keyTrader;
    if (variant === "studio" && t(keyStudio) !== keyStudio) return keyStudio;
    return `landing.${suffix}`;
  };
  // earning_ways è una lista di stringhe localizzate (per ruolo se disponibile)
  const earningWaysKey = variant === "re" ? "landing.earning_ways_re"
    : variant === "trader" ? "landing.earning_ways_trader"
    : variant === "studio" ? "landing.earning_ways_studio"
    : "landing.earning_ways";
  const earningWays = t(earningWaysKey, { returnObjects: true });

  useEffect(() => {
    api.get("/banners").then(r => setBanners(r.data)).catch(() => {});
  }, []);

  return (
    <div className="bg-cream">
      {/* HERO */}
      <section className="relative min-h-[100svh] overflow-hidden" data-testid="landing-hero">
        <div className="absolute inset-0">
          <img
            src={heroImage}
            alt={isRealEstate ? "Real estate handshake" : "Interior"}
            className="w-full h-full object-cover"
            data-testid="landing-hero-image"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-cream/10 via-cream/40 to-cream" />
        </div>

        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-40 lg:pt-56 pb-28 grid grid-cols-12 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: "easeOut" }}
            className="col-span-12 lg:col-span-8"
          >
            <div className="text-[11px] tracking-[0.3em] uppercase text-slate1 mb-6">{t(k("category"))}</div>
            <h1 className="font-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-black">
              {t(k("hero_line1"))}
              <br />
              <span className="italic font-light text-black">{t(k("hero_line2_em"))}</span>
            </h1>
            <p className="mt-8 max-w-xl text-lg text-black leading-relaxed">
              <Trans i18nKey={k("hero_sub")} />
            </p>

            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to={primaryCta.to} className="group inline-flex items-center gap-3 bg-ink text-cream px-7 py-4 rounded-sm text-[13px] tracking-[0.2em] uppercase hover-lift" data-testid={primaryCta.testid}>
                {primaryCta.label} <ArrowRight size={16} className="transition-transform group-hover:translate-x-1" />
              </Link>
              <Link to="/outlet" className="inline-flex items-center gap-3 border border-ink/20 text-ink px-7 py-4 rounded-sm text-[13px] tracking-[0.2em] uppercase hover:bg-ink hover:text-cream transition-colors" data-testid="hero-cta-outlet">
                {t("landing.cta_explore_outlet")}
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.3 }}
            className="col-span-12 lg:col-span-4 lg:pt-24"
          >
            {isTrader ? (
              <WorldMapCard />
            ) : isRealEstate ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35, ease: "easeOut" }}
                className="bg-white border border-ink/10 p-8 rounded-sm"
                data-testid="re-rewards-summary"
              >
                <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("landing.rewards_re_title")}</div>
                <div className="mt-6 flex items-baseline gap-3">
                  <div className="font-display text-6xl text-ink leading-none">€100</div>
                  <div className="text-xs tracking-[0.2em] uppercase text-slate1">per contatto</div>
                </div>
                <div className="mt-6 h-px bg-ink/10" />
                <ul className="mt-6 space-y-4 text-sm text-ink/85">
                  <li className="flex gap-3">
                    <span className="text-gold mt-1 shrink-0">—</span>
                    <span><Trans i18nKey="landing.value_bullet1_re" /></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold mt-1 shrink-0">—</span>
                    <span><Trans i18nKey="landing.value_bullet2_re" /></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold mt-1 shrink-0">—</span>
                    <span><Trans i18nKey="landing.value_bullet3_re" /></span>
                  </li>
                </ul>
                <div className="mt-6 flex items-center gap-3 text-xs text-slate1">
                  <Sparkles size={14} className="text-gold shrink-0" /> {t("landing.rewards_full_program")}
                </div>
              </motion.div>
            ) : isStudio ? (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.35, ease: "easeOut" }}
                className="bg-white border border-ink/10 p-8 rounded-sm"
                data-testid="studio-rewards-summary"
              >
                <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("landing.rewards_studio_title")}</div>
                <div className="mt-6 flex items-baseline gap-3">
                  <div className="font-display text-6xl text-ink leading-none">2%</div>
                  <div className="text-xs tracking-[0.2em] uppercase text-slate1">{t("landing.rewards_studio_unit")}</div>
                </div>
                <div className="mt-6 h-px bg-ink/10" />
                <ul className="mt-6 space-y-4 text-sm text-ink/85">
                  <li className="flex gap-3">
                    <span className="text-gold mt-1 shrink-0">—</span>
                    <span><Trans i18nKey="landing.value_bullet1_studio" /></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold mt-1 shrink-0">—</span>
                    <span><Trans i18nKey="landing.value_bullet2_studio" /></span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-gold mt-1 shrink-0">—</span>
                    <span><Trans i18nKey="landing.value_bullet3_studio" /></span>
                  </li>
                </ul>
                <div className="mt-6 flex items-center gap-3 text-xs text-slate1">
                  <Sparkles size={14} className="text-gold shrink-0" /> {t("landing.rewards_full_program")}
                </div>
              </motion.div>
            ) : (
              <div className="bg-white border border-ink/10 p-8 rounded-sm" data-testid="reward-wallet-summary">
                <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t("landing.rewards_header")}</div>
                <div className="mt-5 space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-20 shrink-0 flex items-start pt-0.5"><Wallet size={28} strokeWidth={1.5} className="text-ink" /></div>
                    <div>
                      <div className="text-sm font-medium text-ink">{t("landing.rewards_architects_title")}</div>
                      <div className="text-xs text-slate1"><Trans i18nKey="landing.rewards_architects_desc" /></div>
                    </div>
                  </div>
                  <div className="h-px bg-ink/10" />
                  <div className="flex items-start gap-4">
                    <div className="w-20 shrink-0 flex items-start pt-0.5"><Gift size={28} strokeWidth={1.5} className="text-ink" /></div>
                    <div>
                      <div className="text-sm font-medium text-ink">{t("landing.rewards_re_title")}</div>
                      <div className="text-xs text-slate1">{t("landing.rewards_re_desc")}</div>
                    </div>
                  </div>
                  <div className="h-px bg-ink/10" />
                  <div className="flex items-start gap-4">
                    <div className="font-display text-2xl text-ink leading-none w-20 shrink-0 pt-1">★</div>
                    <div>
                      <div className="text-sm font-medium text-ink">{t("landing.rewards_trader_title")}</div>
                      <div className="text-xs text-slate1">{t("landing.rewards_trader_desc")}</div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 flex items-center gap-3 text-xs text-slate1">
                  <Sparkles size={14} className="text-gold shrink-0" /> {t("landing.rewards_full_program")}
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* EARNING WAYS MARQUEE */}
      <section className="border-y border-ink/10 bg-cream2 py-8" data-testid="landing-earning-ways">
        <Marquee gradient={false} speed={36} pauseOnHover>
          {(Array.isArray(earningWays) ? earningWays.concat(earningWays) : []).map((w, i) => (
            <div key={i} className="mx-12 flex items-center gap-4 text-slate1">
              <span className="text-gold text-xl leading-none">◆</span>
              <span className="font-display text-base md:text-lg tracking-[0.2em] uppercase whitespace-nowrap">{w}</span>
            </div>
          ))}
        </Marquee>
      </section>

      {/* VALUE PROPS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 grid grid-cols-12 gap-8" data-testid="landing-value">
        <div className="col-span-12 lg:col-span-5">
          <div className="text-[11px] tracking-[0.3em] uppercase text-slate1">{t(k("value_pre"))}</div>
          <h2 className="mt-4 font-display text-4xl lg:text-5xl font-light text-ink leading-tight">
            {t(k("value_title_line1"))} <span className="italic text-slate1">{t(k("value_title_em"))}</span> {t(k("value_title_line2"))}
          </h2>
          <p className="mt-6 text-slate1 text-lg leading-relaxed">
            {t(k("value_intro"))}
          </p>
          <ul className="mt-6 space-y-4 text-slate1">
            <li className="flex gap-3">
              <span className="text-gold shrink-0">—</span>
              <span><Trans i18nKey={k("value_bullet1")} /></span>
            </li>
            <li className="flex gap-3">
              <span className="text-gold shrink-0">—</span>
              <span><Trans i18nKey={k("value_bullet2")} /></span>
            </li>
            <li className="flex gap-3">
              <span className="text-gold shrink-0">—</span>
              <span><Trans i18nKey={k("value_bullet3")} /></span>
            </li>
          </ul>
        </div>

        <div className="col-span-12 lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-6">
          {(isTrader
            ? [
                { icon: Truck, title: t("landing.feat_logistics_t"), desc: t("landing.feat_logistics_d") },
                { icon: Package, title: t("landing.feat_b2b_t"), desc: t("landing.feat_b2b_d") },
                { icon: Flame, title: t("landing.feat_stock_t"), desc: t("landing.feat_stock_d") },
                { icon: Wallet, title: t("landing.feat_trader_terms_t"), desc: t("landing.feat_trader_terms_d") },
              ]
            : isRealEstate
            ? [
                { icon: Wallet, title: t("landing.feat_re_100_t"), desc: t("landing.feat_re_100_d") },
                { icon: Sparkles, title: t("landing.feat_re_pay_t"), desc: t("landing.feat_re_pay_d") },
                { icon: Package, title: t("landing.feat_re_brand_t"), desc: t("landing.feat_re_brand_d") },
                { icon: Truck, title: t("landing.feat_re_turnkey_t"), desc: t("landing.feat_re_turnkey_d") },
              ]
            : [
                { icon: Wallet, title: t("landing.feat_wallet_2_t"), desc: t("landing.feat_wallet_2_d") },
                { icon: Package, title: t("landing.feat_b2b_t"), desc: t("landing.feat_b2b_d") },
                { icon: Flame, title: t("landing.feat_stock_t"), desc: t("landing.feat_stock_d") },
                { icon: Truck, title: t("landing.feat_white_glove_t"), desc: t("landing.feat_white_glove_d") },
              ]
          ).map((f, i) => (
            <div key={i} className="bg-white border border-ink/10 p-8 hover-lift rounded-sm" data-testid={`value-${i}`}>
              <f.icon className="text-ink" size={22} />
              <div className="mt-6 font-display text-xl text-ink">{f.title}</div>
              <p className="mt-3 text-sm text-slate1 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BANNERS */}
      {banners.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-10 pb-24">
          <RotatingBanners banners={banners} variant="landing" testid="landing-banners" />
        </section>
      )}

      {/* CTA */}
      <section className="bg-ink text-cream py-24" data-testid="landing-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-12 gap-8 items-center">
          <div className="col-span-12 lg:col-span-7">
            <h3 className="font-display text-4xl lg:text-5xl font-light leading-tight">
              {t(k("cta_section_title"))} <span className="italic text-cream/60">{t(k("cta_section_em"))}</span>
            </h3>
          </div>
          <div className="col-span-12 lg:col-span-5 flex lg:justify-end">
            <Link to={footerCta.to} className="inline-flex items-center gap-3 bg-cream text-ink px-8 py-4 rounded-sm text-[13px] tracking-[0.2em] uppercase hover-lift" data-testid={footerCta.testid}>
              {footerCta.label} <ArrowRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
